/**
 * `/commissions` — the schemes, and the leads assigned to them.
 *
 * ⛔ **THIS IS THE ONLY SCREEN IN EMERGE THAT DISCLOSES PAY.** Owner decision 24,
 * and backend migration `205_add_commissions_permissions.sql` is the whole of it:
 * `commissions` is catalogued as a REAL module at `/api/v1/commissions`, the
 * router carries **no `requireRole` anywhere**, every handler reaches
 * `requirePermission`, and only `management` and `super-admin` hold a grant.
 * `accounts` — the tier that raises invoices and credit notes and reads every
 * revenue surface — holds **nothing**, deliberately.
 *
 * ── WHAT THAT MEANS FOR THIS FILE, IN THREE RULES ───────────────────────────
 *
 * 1. ⛔ **`commissions` IS NOT A NAV-SURFACE VIEW CONTROL, and the difference is
 *    the entire point.** The nine surfaces of migrations 108, 145 and 155 hide a
 *    page this app draws while the API behind them answers the same to
 *    everybody — *hiding a page is not refusing a request*. Here the grant IS
 *    the refusal. So a reader without it does not merely lose a nav row: they
 *    meet a real refusal panel, in {@link RefusalPanel}, naming what they may do
 *    rather than an empty table that reads as "there are no schemes".
 *    ⛔ **And nothing here may be added to `NAV_SURFACE_MODULE`** — that set
 *    forces `create`/`update`/`delete` to a hard `false`, which would delete
 *    every affordance this module exists to gate and leave a reader believing
 *    the figures were protected by a view control. The migration says so in as
 *    many words.
 *
 * 2. ⛔ **THE 403 IS STILL HANDLED, AND THAT IS NOT BELT-AND-BRACES.** Every
 *    control is gated on `ability.canCreate/canUpdate/canDelete('commissions')`
 *    — never on `user.role` — but a hidden control is an OPTIMISATION and the
 *    server's refusal is the GUARANTEE. A role or a grant can change under a
 *    live access token, which lasts **5.28 days** here, so a bundle loaded on
 *    Monday is still gating on Monday's answer on Friday. A 403 body carries
 *    **no `details`**, so it renders as a PANEL-LEVEL banner and never per
 *    field: there is no field it belongs to, and keying it on `''` would make
 *    `fieldErrors` non-empty and print *"one field needs attention, highlighted
 *    below"* with nothing highlighted.
 *
 * 3. ⚠️ **AN ABSENT SCOPE READS AS ALLOWED, so this page is not the guarantee
 *    even for READ.** On a module that is not a nav surface, `can()` resolves an
 *    absent scope as allowed for `read` and DENIED for every write. An unseeded
 *    catalogue, an older API or a failed `/permissions/me` therefore all leave
 *    this page fetching — and the server refuses. That is the safe direction and
 *    it is why rule 2 exists.
 *
 * ── TWO SECTIONS, ONE MOUNTED AT A TIME ─────────────────────────────────────
 *
 * Schemes are the TEMPLATES; assignments are scheme × lead × period. They are
 * two sections behind a `?tab=` pill strip, following `/operations`:
 *
 * ⚠️ **THE DEFAULT IS SPELLED BY THE KEY'S ABSENCE and NOTHING IS WRITTEN ON
 * ARRIVAL.** `/commissions` and `/commissions?tab=schemes` are one URL for one
 * screen rather than two for it — and an arrival write would race any
 * saved-filter seeding into the same query string off a snapshot taken before
 * it, where one of the two loses silently and the reader gets the right screen
 * with somebody else's filters.
 *
 * ⚠️ **ONE section fetches, never both with one hidden.** Two mounted sections
 * are two pagers writing one `page` key — which is the half that breaks — and
 * two fetches against a limit shared by the whole office.
 * ⚠️ *SUPERSEDED: "a limit of 120 requests per 60 seconds."* It is
 * **12,000/60s** (corrected 2026-09-17).
 *
 * 🔴 **SWITCHING CLEARS EVERY KEY EITHER SECTION OWNS — see
 * {@link CLEARED_ON_SWITCH}, and `page` is the one that matters most.** Both
 * sections page, so carrying `?page=3` from a long scheme list into a short
 * assignment list asks for page 3 of a shorter result — usually empty, which
 * reads as *"this scheme has nobody on it"* when the rows are all on page 1.
 * This repo shipped exactly that on `/users` and the house rule ("reset to page
 * 1") had simply never been applied there.
 *
 * ── WHAT THIS PAGE DELIBERATELY DOES NOT DO ─────────────────────────────────
 *
 * ⛔ **NO RUNS, NO STATEMENTS, NO APPROVE CONTROL.** Backend migration 204
 * builds the run and the statement and 205 grants a fifth `approve` action, but
 * `ActionSlug` in `@/lib/permissions` does not carry `approve` on this module,
 * so the ability model cannot answer *"may this person approve"* at all. There
 * is no run surface in this app to put a control on, and a button that opens
 * nothing is the affordance-with-one-outcome this repo refuses everywhere
 * (Xero's `sync`, DocuSign's absent connect). **When a run surface lands, the
 * rule for whoever builds it is written here rather than rediscovered: render
 * the control and let the 403 arrive.**
 *
 * ⛔ **`listCommissionModels` AND `listCommissionGateShapes` ARE NOT CALLED
 * HERE, and that is a decision rather than an omission.** Every row already
 * carries its own `model` and `gateShape` embeds — read the object, write the
 * id, as every other master in this app — so fetching a two-row registry per
 * page load would spend a request from a shared budget to render a name that is
 * already on the row. Those two reads belong to the FORM MODALS, which have to
 * OFFER the choices rather than render one. ⚠️ If either embed ever turns out
 * to be absent from the wire, that is the moment this decision changes, and it
 * changes here rather than at a cell.
 *
 * ⛔ **NO AGGREGATE ANYWHERE.** A scheme's currency is its own and **nothing
 * converts it** (migration 202: *"Lindsay is paid in USD and Daryna in GBP"*).
 * One page legitimately holds several codes, so a column total would be wrong
 * by construction rather than merely imprecise — which is why every figure here
 * renders with its code and `formatDecimalString` never sees a `Number()`.
 *
 * ── ONE WRITER PER QUERY KEY ────────────────────────────────────────────────
 *
 * | key                    | section     | its ONE writer                     |
 * | ---------------------- | ----------- | ---------------------------------- |
 * | `tab`                  | the page    | the pill strip                     |
 * | `search`               | schemes     | the search box                     |
 * | `isActive`             | schemes     | the Active filter                  |
 * | `schemeId`             | assignments | the Scheme filter, and the Schemes |
 * |                        |             | table's Assignments cell           |
 * | `userId`, `periodId`   | assignments | arrived at BY LINK — a badge, and  |
 * |                        |             | no control of their own            |
 * | `page`                 | both        | the pager of whichever is mounted  |
 *
 * ── COLUMN FUNNELS AND THE COLUMN MANAGER (owner 2026-10-25) ────────────────
 *
 * Both tables carry a funnel per data column (`useTableFilters`, memory ids
 * `commission-schemes` / `commission-assignments`) and a `ColumnManager`
 * (`preferences.columns.commissions.schemes` / `.assignments`). The funnels
 * are the house per-column filters: their state lives in the account's filter
 * memory, like every other table's funnels, NOT in this query string. They
 * filter in the browser, so each section reads EVERY page (`fetchAllPages`) and
 * pages locally. ⚠️ The page number is component state, not a query key.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useSearchParams } from 'react-router-dom';
import { AssignmentFormModal, SchemeFormModal } from '@/components/commissions';
import {
  ChevronIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
// ⛔ THE NAMES ARE FETCHED, NEVER MAPPED. `f_billing_region_defaults` holds four
// admin-editable name columns, so a literal `{united_kingdom: 'United
// Kingdom', …}` in `src/` is a second copy of wording the server owns —
// correct on the day it is written and silently wrong after the first rename,
// with nothing failing. This resolver is what replaces it, and it already maps
// `'none'` to `NO_REGION_LABEL`.
import { useBillingRegionLabels } from '@/lib/billing-region';
import {
  COMMISSION_VISIBILITY_NOTE,
  TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE,
  formatPercent,
  isUnbandedRegion,
  modelExplanation,
  newBusinessGateBasisLabel,
  schemeMeasuresRegions,
} from '@/lib/commissions';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDecimalString, pluralise } from '@/lib/format';
import { denialReason, type ModuleSlug } from '@/lib/permissions';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import type {
  CommissionAssignment,
  CommissionAssignmentListParams,
  CommissionScheme,
  CommissionSchemeListParams,
  Paginated,
  TargetBillingRegion,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Badge,
  Button,
  ColumnManager,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  PageHeader,
  Panel,
  MultiCombobox,
  PanelFooter,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type ComboboxOption,
  type FilterableTHProps,
  type FrozenSide,
} from '@/components/ui';
import { Money as UiMoney } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';

/**
 * The module every gate on this page asks about.
 *
 * ⛔ **Named once so no call site can reach for a neighbour's slug.** Borrowing
 * `users` or `targets` here would be the `integrations`-borrowing-`companies`
 * mistake, where granting one capability silently grants a second nobody was
 * asked about — and the second one here is everybody's pay.
 */
const COMMISSIONS: ModuleSlug = 'commissions';

/** Rows per page of the (client-side) pager on both tables. */
const PER_PAGE = 20;
/** The endpoints' `perPage` ceiling, used to read every page of a list. */
const FETCH_PER_PAGE = 100;
/** One page of scheme options for the assignments filter; the ceiling is 100. */
const OPTION_PER_PAGE = 100;
/** Fewer than a full page — enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;
/** `search` is capped server-side; a longer term is a 422, not a truncation. */
const MAX_SEARCH = 240;

/* -------------------------------------------------------------------------- *
 * The two sections
 * -------------------------------------------------------------------------- */

type Tab = 'schemes' | 'assignments';

const TABS: readonly { value: Tab; label: string }[] = [
  { value: 'schemes', label: 'Schemes' },
  { value: 'assignments', label: 'Assignments' },
];

const TAB_PARAM = 'tab';

/**
 * 🔴 **EVERY QUERY KEY EITHER SECTION OWNS, CLEARED ON EVERY SWITCH.**
 *
 * Both sections live in ONE query string, so a key left behind applies one
 * section's narrowing to the other's list. A short list is not a smaller version
 * of the right one — it is a filtered table under an unfiltered heading, which
 * is this codebase's most repeated failure.
 *
 * ⚠️ **`page` IS THE DANGEROUS ONE AND IS SHARED BY BOTH.** Switching from page
 * 3 of the schemes into a shorter assignment list asks for page 3 of that, which
 * is usually empty — and an empty table reads as *"nobody is assigned"* rather
 * than as a pager left where somebody else put it.
 *
 * ⚠️ **`schemeId` IS CLEARED TOO, EVEN THOUGH ONLY ONE SECTION READS IT.** The
 * Schemes table's Assignments cell WRITES it on the way across (see
 * {@link AssignmentsCell}), and that write happens in the same
 * `URLSearchParams` as the tab change rather than as a second `setSearchParams`
 * — two writes from one render both start from the same stale snapshot and only
 * the last survives, with nothing failing.
 */
const CLEARED_ON_SWITCH = ['search', 'isActive', 'schemeId', 'userId', 'periodId', 'page'] as const;

/* -------------------------------------------------------------------------- *
 * Reading the query string
 * -------------------------------------------------------------------------- */

/**
 * Anything that is not a positive integer is DROPPED rather than forwarded.
 *
 * `?schemeId=` is a 422 on a `.strict()` schema, not "no filter", and a pasted
 * `?schemeId=abc` would take the whole list down — which renders as an empty
 * table and reads as "this scheme has nobody on it".
 */
function parseId(raw: string | null): number | null {
  if (!raw) return null;
  return /^[1-9]\d*$/.test(raw) ? Number(raw) : null;
}

/**
 * A CSV of ids (owner 2026-10-28: every filter takes several values), each
 * dropped unless it is a positive integer, deduplicated. A link naming one id
 * parses to a one-element list. `[]` is "not filtered".
 */
function parseIdList(raw: string | null): number[] {
  if (!raw) return [];
  const ids: number[] = [];
  for (const part of raw.split(',')) {
    const id = parseId(part.trim());
    if (id !== null && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** `''` is "either", a third request distinct from both `true` and `false`. */
function parseTriFlag(raw: string | null): '' | 'true' | 'false' {
  return raw === 'true' || raw === 'false' ? raw : '';
}

/* -------------------------------------------------------------------------- *
 * Columns
 * -------------------------------------------------------------------------- */

interface ColumnMeta {
  label: string;
  width: number;
  numeric?: boolean;
}

/** `TD`'s `px-3`, twice. A column's width is its content minimum plus this. */
const CELL_PADDING_X = 24;
/** The one FLUID column on each table takes what the fixed ones leave. */
const NAME_MIN_CONTENT = 240;

type SchemeColumnKey =
  | 'name'
  | 'model'
  | 'personalRate'
  | 'regionalRate'
  | 'newBusinessRate'
  | 'newBusinessGate'
  | 'gate'
  | 'currency'
  | 'bands'
  | 'assignments'
  | 'isActive'
  | 'actions';

/*
 * ⚠️ KEYED ON THE UNION, NOT ON `string`. This app builds with
 * `noUncheckedIndexedAccess`, so indexing a `Record<string, …>` yields
 * `ColumnMeta | undefined` at every site below — including the one that sums the
 * table's floor. A `!` at each site asserts away a real check and goes on
 * asserting it if somebody removes an entry here but not from the key order.
 */
const SCHEME_COLUMNS: Record<SchemeColumnKey, ColumnMeta> = {
  name: { label: 'Scheme', width: NAME_MIN_CONTENT + CELL_PADDING_X },
  model: { label: 'Model', width: 170 },
  personalRate: { label: 'Personal rate', width: 130, numeric: true },
  regionalRate: { label: 'Regional rate', width: 130, numeric: true },
  newBusinessRate: { label: 'New business rate', width: 160, numeric: true },
  newBusinessGate: { label: 'New business gate', width: 200 },
  gate: { label: 'Gate', width: 180 },
  currency: { label: 'Currency', width: 100 },
  bands: { label: 'Bands', width: 240 },
  assignments: { label: 'Assignments', width: 140, numeric: true },
  isActive: { label: 'Active', width: 100 },
  actions: { label: '', width: 96 },
};

const SCHEME_COLUMN_KEYS = [
  'name',
  'model',
  'personalRate',
  'regionalRate',
  'newBusinessRate',
  'newBusinessGate',
  'gate',
  'currency',
  'bands',
  'assignments',
  'isActive',
  'actions',
] as const satisfies readonly SchemeColumnKey[];

/**
 * The new business rate as displayed text, or `null` when the record came from
 * an older API without the key (`undefined` is never read as 0).
 */
function newBusinessRateText(scheme: CommissionScheme): string | null {
  return scheme.newBusinessRatePercent === undefined
    ? null
    : formatPercent(scheme.newBusinessRatePercent);
}

/** The new business gate basis label, or `null` on an older API (key absent). */
function newBusinessGateText(scheme: CommissionScheme): string | null {
  return scheme.newBusinessGateBasis === undefined
    ? null
    : newBusinessGateBasisLabel(scheme.newBusinessGateBasis);
}

/**
 * The overrides the basis actually reads, as `shape · x% personal · y% regional`.
 * `null` when the basis reads none of them, or every one falls back to the scheme.
 */
function newBusinessGateOverridesText(scheme: CommissionScheme): string | null {
  const basis = scheme.newBusinessGateBasis;
  const personal = basis === 'personal' || basis === 'both';
  const regional = basis === 'regional' || basis === 'both';
  if (!personal && !regional) return null;
  const parts: string[] = [];
  if (scheme.newBusinessGateShape) parts.push(scheme.newBusinessGateShape.name);
  if (personal && scheme.newBusinessPersonalGatePercent != null) {
    parts.push(`${formatPercent(scheme.newBusinessPersonalGatePercent)} personal`);
  }
  if (regional && scheme.newBusinessRegionalGatePercent != null) {
    parts.push(`${formatPercent(scheme.newBusinessRegionalGatePercent)} regional`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

type AssignmentColumnKey =
  | 'lead'
  | 'period'
  | 'scheme'
  | 'dates'
  | 'personalTarget'
  | 'regionalTarget'
  | 'actions';

const ASSIGNMENT_COLUMNS: Record<AssignmentColumnKey, ColumnMeta> = {
  lead: { label: 'Lead', width: NAME_MIN_CONTENT + CELL_PADDING_X },
  period: { label: 'Period', width: 160 },
  scheme: { label: 'Scheme', width: 220 },
  dates: { label: 'Dates', width: 210 },
  personalTarget: { label: 'Personal target', width: 170, numeric: true },
  regionalTarget: { label: 'Regional target', width: 190, numeric: true },
  actions: { label: '', width: 96 },
};

const ASSIGNMENT_COLUMN_KEYS = [
  'lead',
  'period',
  'scheme',
  'dates',
  'personalTarget',
  'regionalTarget',
  'actions',
] as const satisfies readonly AssignmentColumnKey[];

/**
 * Summed from the declared widths, so the floor cannot drift from the header.
 *
 * ⚠️ **IT REACHES THE DOM THROUGH `style`, NEVER A CLASS.** Tailwind v4 scans
 * SOURCE TEXT, so an interpolated `min-w-[${n}px]` emits no rule at all — the
 * class lands in the DOM and does nothing, which reads as a table that simply
 * chose not to hold its floor.
 */
function tableFloor<K extends string>(
  keys: readonly string[],
  columns: Record<K, ColumnMeta>,
): number {
  // Takes the VISIBLE keys, so hiding a column narrows the floor with it.
  const byKey: Partial<Record<string, ColumnMeta>> = columns;
  return keys.reduce((total, key) => total + (byKey[key]?.width ?? 0), 0);
}

function schemeLabelOf(key: string): string {
  if (key === ACTIONS_KEY) return 'Actions';
  const byKey: Partial<Record<string, ColumnMeta>> = SCHEME_COLUMNS;
  return byKey[key]?.label ?? key;
}

function assignmentLabelOf(key: string): string {
  if (key === ACTIONS_KEY) return 'Actions';
  const byKey: Partial<Record<string, ColumnMeta>> = ASSIGNMENT_COLUMNS;
  return byKey[key]?.label ?? key;
}

/*
 * Column manager wiring (owner 2026-10-25). The keys above are the stored
 * column ids under `preferences.columns.<scope>` — never rename one.
 * Actions is locked (it holds the row controls) and pinned to the right edge.
 */
const ACTIONS_KEY = 'actions';
const LOCKED_KEYS: readonly string[] = [ACTIONS_KEY];
const PINNED_KEYS: readonly string[] = [ACTIONS_KEY];
const SCHEME_LAYOUT_SCOPE = 'commissions.schemes';
const ASSIGNMENT_LAYOUT_SCOPE = 'commissions.assignments';

function actionsLockReason(key: string): string | undefined {
  return key === ACTIONS_KEY
    ? 'Actions holds the Edit and Delete controls for each row, so it cannot be hidden'
    : undefined;
}

/** Visible keys in layout order, with the pinned actions lane forced last. */
function renderOrder(visibleKeys: readonly string[]): string[] {
  return [...visibleKeys.filter((key) => key !== ACTIONS_KEY), ACTIONS_KEY];
}

interface FrozenModel {
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}

/** Empty for an unfrozen column, so it never becomes a positioned cell. */
function frozenPropsFor(
  key: string,
  { frozenSideOf, frozenOffsets }: FrozenModel,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const frozen = frozenSideOf(key);
  if (frozen === undefined) return {};
  return { frozen, frozenOffset: frozenOffsets[key] ?? 0 };
}

/**
 * Every page of a list, read `FETCH_PER_PAGE` at a time.
 *
 * The column filters run in the browser (neither endpoint has a param per
 * column), so they must see EVERY row, not one page of 20 — otherwise an empty
 * result means "not on this page" while looking like "not anywhere". These
 * lists are tiny (a handful of schemes, one assignment per lead per year), so
 * the whole list is read and paged locally. The server-side narrowing that
 * already existed (search, status, scheme, lead, period) is still sent.
 */
async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<Paginated<T>>,
): Promise<T[]> {
  const first = await fetchPage(1);
  const data = [...first.data];
  for (let page = 2; page <= first.meta.totalPages; page += 1) {
    const next = await fetchPage(page);
    data.push(...next.data);
  }
  return data;
}

/** The stored filter state of every column, as one string — resets the pager. */
function filterSignature(
  keys: readonly string[],
  headerFor: (key: string) => FilterableTHProps,
): string {
  return keys.map((key) => JSON.stringify(headerFor(key).filter?.value ?? null)).join('|');
}

/** A money figure as filter text: the exact string grouped, then its code. */
function moneyText(value: string | null, currency: string): string | null {
  return value === null ? null : `${formatDecimalString(value)} ${currency}`;
}

/* -------------------------------------------------------------------------- *
 * Shared cells
 * -------------------------------------------------------------------------- */

/** Resolves one of a scheme's bands to what a reader calls it. */
type BandLabeller = (region: string) => string;

/**
 * What to call the lead an assignment names.
 *
 * ⚠️ **`name` IS NULLABLE AND `''` IS REACHABLE**, so neither may reach a value
 * slot: an empty string there reads as a MISSING record rather than as a
 * badly-filled one, and `null` would print as nothing at all. The email is the
 * next-best identity because `d_users.email` is `NOT NULL UNIQUE`; the id behind
 * it is the last resort and is deliberately marked as an id rather than dressed
 * up as a name.
 *
 * ⛔ **Never "Unknown" or "Anonymous".** Both are spoken for elsewhere in this
 * app — the audit log uses the second for an unauthenticated caller — and a row
 * here always names a real person, whatever this client was told about them.
 */
function leadLabel(user: CommissionAssignment['user']): string {
  return (user.name ?? '').trim() || user.email.trim() || `Staff #${user.id}`;
}

/**
 * The band names, from the master, widened to accept the wire's `string`.
 *
 * ⛔ **THE NAMES ARE FETCHED AND THERE IS NO LABEL MAP** —
 * `f_billing_region_defaults` holds four admin-editable name columns, so a
 * literal `{united_kingdom: 'United Kingdom', …}` anywhere in `src/` is a second
 * copy of wording the server owns: correct on the day it is written and silently
 * wrong after the first rename, with nothing failing.
 *
 * ⚠️ **THE ONE CAST, AND WHY IT IS SAFE RATHER THAN CONVENIENT.**
 * `CommissionScheme.billingRegions` is `string[]` on the wire — migration 202
 * stores `VARCHAR(20)` and validates in the SERVICE against the shared list,
 * deliberately NOT as an ENUM, because a MySQL ENUM stores the member index and
 * can never be reordered. So a band this client has never heard of is a
 * reachable value, and `useBillingRegionLabels` is **total over any string**: it
 * answers the master's name where it has one and **falls back to the raw slug**
 * otherwise. That fallback is what makes the cast safe — a sixth band renders as
 * `latin_america` rather than throwing, which is unmistakable and is exactly the
 * string somebody needs in order to go and look.
 *
 * ⛔ **Do not "tighten" this by narrowing `billingRegions` to the five-member
 * union.** That would make a band added on the server a TYPE ERROR here and a
 * blank chip in production — a client asserting a vocabulary the column does not
 * have.
 */
function useBandLabels(): BandLabeller {
  const regionLabel = useBillingRegionLabels();
  return useCallback(
    (region: string): string => regionLabel(region as TargetBillingRegion),
    [regionLabel],
  );
}

function CommissionIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 3v18" />
      <path d="M17 7H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H6" />
    </svg>
  );
}

/**
 * A money figure **with its currency code, always**.
 *
 * ⛔ **THE CODE IS NOT DECORATION.** A scheme's currency is its own and nothing
 * converts it, so a bare number beside another bare number is an invitation to
 * add two different currencies together. `formatDecimalString` groups the exact
 * string without ever touching a float — ⛔ never `Number()` on one of these.
 */
function Money({ value, currency }: { value: string; currency: string }) {
  return <UiMoney amount={value} currency={currency} />;
}

/**
 * The row's two controls, gated on `ability` and DISABLED rather than hidden
 * when the reader may not use them.
 *
 * ⚠️ **DISABLED, NOT WITHHELD, BECAUSE THE REFUSAL IS ABOUT *THIS READER*.**
 * The DocuSign `Check status` precedent: where the only possible outcome is an
 * error for everybody the affordance must be ABSENT (Xero's `sync`), but where
 * the action is real and the refusal is personal a reader cannot tell a gated
 * control from a broken one unless the reason is beside it.
 *
 * 🔴 **THE REASON GOES ON A WRAPPER `span`, NEVER ON THE BUTTON.** `Button`
 * carries `disabled:pointer-events-none`, so a `title` on a disabled one **never
 * receives the hover and never renders** — this repo has shipped two buttons
 * whose why-disabled sentence has never once been seen by anybody.
 */
function RowActions({
  onEdit,
  onDelete,
  editDenial,
  deleteDenial,
  label,
}: {
  onEdit: () => void;
  onDelete: () => void;
  editDenial: string | null;
  deleteDenial: string | null;
  label: string;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <span {...(editDenial ? { title: editDenial } : {})}>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={`Edit ${label}`}
          leadingIcon={<PencilIcon />}
          disabled={editDenial !== null}
          onClick={onEdit}
        />
      </span>
      <span {...(deleteDenial ? { title: deleteDenial } : {})}>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={`Delete ${label}`}
          leadingIcon={<TrashIcon />}
          disabled={deleteDenial !== null}
          onClick={onDelete}
        />
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- *
 * The Bands cell — the one worth extra care
 * -------------------------------------------------------------------------- */

/**
 * 🔴 **AN EMPTY BAND LIST IS A REAL CONFIGURATION AND MUST NOT RENDER AS AN EM
 * DASH.**
 *
 * Migration 202 states it: *"A SCHEME WITH NO ROWS HERE MEASURES NO REGIONAL
 * REVENUE AT ALL, so its pool is zero and every lead on it is paid their
 * personal commission alone. That is a legal configuration — a personal-only
 * scheme — and the service permits it deliberately rather than requiring at
 * least one band."*
 *
 * A dash is this app's glyph for *nothing recorded*, so drawing one here would
 * report a deliberate personal-only scheme as an unfinished record — and every
 * lead on it as owed a regional bonus that the arithmetic will never compute.
 * The cell states the CONSEQUENCE instead, as real text.
 *
 * ⚠️ **AND THE HONEST HALF: THE WIRE CANNOT TELL A DELIBERATE EMPTY SET FROM A
 * FORGOTTEN ONE.** The same migration says so — *"it is also indistinguishable
 * from somebody having forgotten to pick a region"*. There is no third state on
 * the column and no flag beside it, so no rendering can separate them. The cell
 * says what the scheme WILL DO, which is the fact that is knowable, and the
 * `title` says plainly that the record does not answer whether it was intended.
 * ⛔ **Do not "fix" this by inventing a warning tone**: an amber badge on every
 * personal-only scheme would nag about a configuration somebody chose.
 *
 * ⚠️ **THE SENTENCE IS NOT HELPER TEXT.** It names a state the reader cannot
 * otherwise see — an empty cell and a personal-only scheme are the same pixels —
 * which is the documented exception. It is attached to a VALUE, not to a control.
 *
 * ⚠️ **`'none'` IS A REAL BAND, NOT AN ABSENCE.** It is the companies with no
 * country of record, and owner decision 13 gives them a target and an owner so
 * the regions sum to the business. `useBillingRegionLabels` already renders it
 * as *"No billing region recorded"* — a phrase that reads like an absence in a
 * list of bands — so `isUnbandedRegion` is what puts a `title` on that one chip
 * saying it is a population rather than a gap. ⛔ **It must never be folded
 * together with a scheme that has no rows at all.**
 */
function BandsCell({
  scheme,
  regionLabel,
}: {
  scheme: CommissionScheme;
  /** See {@link useBandLabels} for why this takes a bare `string`. */
  regionLabel: BandLabeller;
}) {
  if (!schemeMeasuresRegions(scheme)) {
    return (
      <Badge
        tone="neutral"
        title={
          'This scheme measures no regional revenue, so its pool is zero and every lead on it is ' +
          'paid their personal commission alone. The record does not say whether that was ' +
          'intended or whether a region was never picked.'
        }
      >
        Personal only
      </Badge>
    );
  }

  return (
    <span className="flex flex-wrap gap-1">
      {scheme.billingRegions.map((region) => (
        <Badge
          key={region}
          tone="neutral"
          variant="outline"
          {...(isUnbandedRegion(region)
            ? {
                title:
                  'A real band: the companies with no country of record. Their revenue counts ' +
                  'towards this scheme like any other band.',
              }
            : {})}
        >
          {regionLabel(region)}
        </Badge>
      ))}
    </span>
  );
}

/**
 * How many leads are on this scheme, and a way through to them.
 *
 * ⚠️ **THE COUNT IS THE POINT, NOT DECORATION.** Editing a scheme reaches every
 * assignment on it that has not been run — migration 202 is explicit that
 * approval freezes a RUN and protects every year already paid, and protects
 * nothing about a year configured and not yet run. So the number beside a scheme
 * is how many people an edit here can reach, which is the fact
 * {@link TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE} states once at the section.
 *
 * ⚠️ **`0` IS PRINTED AS `0`, NOT AS A DASH.** A scheme nobody holds is an
 * ordinary state — a template set up ahead of a hire — and a dash there would
 * read as *"we did not count"*, which is a different fact entirely.
 */
function AssignmentsCell({
  scheme,
  onOpenAssignments,
}: {
  scheme: CommissionScheme;
  onOpenAssignments: (schemeId: number) => void;
}) {
  if (scheme.assignmentCount === 0) {
    return <span className="tabular text-fg-muted">0</span>;
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onOpenAssignments(scheme.id)}
      title={`Show the ${pluralise(scheme.assignmentCount, 'lead')} assigned to ${scheme.name}`}
    >
      <span className="tabular">{scheme.assignmentCount}</span>
    </Button>
  );
}

/* -------------------------------------------------------------------------- *
 * Scheme row cells
 * -------------------------------------------------------------------------- */

function SchemeCell({
  columnKey,
  scheme,
  regionLabel,
  onEdit,
  onDelete,
  onOpenAssignments,
  editDenial,
  deleteDenial,
}: {
  columnKey: SchemeColumnKey;
  scheme: CommissionScheme;
  regionLabel: BandLabeller;
  onEdit: () => void;
  onDelete: () => void;
  onOpenAssignments: (schemeId: number) => void;
  editDenial: string | null;
  deleteDenial: string | null;
}) {
  switch (columnKey) {
    case 'name':
      return (
        /*
         * The content minimum is an INLINE STYLE rather than a `min-w-[240px]`
         * class, and that is forced: an interpolated arbitrary value emits no
         * rule, and a hand-written literal here is a fourth list that can drift
         * from `SCHEME_COLUMNS`.
         */
        <div style={{ minWidth: NAME_MIN_CONTENT }}>
          <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{scheme.name}</p>
          {scheme.description && (
            <p className="mt-0.5 m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              {scheme.description}
            </p>
          )}
        </div>
      );
    case 'model':
      /*
       * ⚠️ **THE NAME IS RENDERED AND THE EXPLANATION COMES FROM THE FLAG.**
       * `modelExplanation` reads `netsPersonalFromPool`, which migration 201
       * calls *"the only thing the arithmetic may read"* — ⛔ never the slug and
       * never the name. Two models both carrying `0` would make Offset compute
       * as Top-up and overpay every year, with every screen looking like money.
       *
       * ⚠️ **The explanation is NOT helper text.** *Top-up* and *Offset* are two
       * words for two different pay formulas and the difference between them is
       * the personal commission; the sentence names a consequence nothing else
       * on the row states.
       */
      return (
        <div className="min-w-0">
          <p className="m-0 text-sm text-fg">{scheme.model.name}</p>
          <p className="mt-0.5 m-0 text-2xs leading-snug text-fg-muted [overflow-wrap:anywhere]">
            {modelExplanation(scheme.model)}
          </p>
        </div>
      );
    case 'personalRate':
      return <span className="tabular">{formatPercent(scheme.personalRatePercent)}</span>;
    case 'regionalRate':
      return <span className="tabular">{formatPercent(scheme.regionalRatePercent)}</span>;
    case 'newBusinessRate':
      return <span className="tabular">{newBusinessRateText(scheme) ?? '—'}</span>;
    case 'newBusinessGate': {
      const overrides = newBusinessGateOverridesText(scheme);
      return (
        <div className="min-w-0">
          <p className="m-0 text-sm text-fg-secondary">{newBusinessGateText(scheme) ?? '—'}</p>
          {overrides && (
            <p className="mt-0.5 m-0 text-xs text-fg-muted tabular">{overrides}</p>
          )}
        </div>
      );
    }
    case 'gate':
      /*
       * The shape, then the two thresholds it applies to — personal first,
       * because that is the order they are read in everywhere else on this page
       * and in the formula itself.
       *
       * ⚠️ **A gate ABOVE 100% is a reachable business rule** (pay only on
       * over-achievement), so nothing here treats one as an error.
       */
      return (
        <div className="min-w-0">
          <p className="m-0 text-sm text-fg-secondary">{scheme.gateShape.name}</p>
          <p className="mt-0.5 m-0 text-xs text-fg-muted tabular">
            {formatPercent(scheme.personalGatePercent)}
            <span className="mx-1 text-fg-subtle">personal</span>
            <span className="mx-0.5 text-fg-subtle">·</span>
            {formatPercent(scheme.regionalGatePercent)}
            <span className="ml-1 text-fg-subtle">regional</span>
          </p>
        </div>
      );
    case 'currency':
      return <span className="tabular">{scheme.currency}</span>;
    case 'bands':
      return <BandsCell scheme={scheme} regionLabel={regionLabel} />;
    case 'assignments':
      return <AssignmentsCell scheme={scheme} onOpenAssignments={onOpenAssignments} />;
    case 'isActive':
      /*
       * ⚠️ **Retiring is the supported removal on this table**, because a scheme
       * an assignment names cannot be deleted at all (`ON DELETE RESTRICT` in
       * migration 203). So this column is not decoration: it is the state a
       * refused delete sends somebody to change.
       */
      return scheme.isActive ? (
        <Badge tone="success">Active</Badge>
      ) : (
        <Badge tone="neutral">Retired</Badge>
      );
    case 'actions':
      return (
        <RowActions
          onEdit={onEdit}
          onDelete={onDelete}
          editDenial={editDenial}
          deleteDenial={deleteDenial}
          label={scheme.name}
        />
      );
    default:
      return null;
  }
}

/** The scheme columns a card shows as a labelled `dl` row. */
type SchemeCardKey =
  | 'model'
  | 'personalRate'
  | 'regionalRate'
  | 'newBusinessRate'
  | 'newBusinessGate'
  | 'gate'
  | 'currency'
  | 'assignments';

const SCHEME_CARD_KEYS: ReadonlySet<string> = new Set<SchemeCardKey>([
  'model',
  'personalRate',
  'regionalRate',
  'newBusinessRate',
  'newBusinessGate',
  'gate',
  'currency',
  'assignments',
]);

function isSchemeCardKey(key: SchemeColumnKey): key is SchemeCardKey {
  return SCHEME_CARD_KEYS.has(key);
}

/** A card's compact value for one column — the same facts as the table cell. */
function SchemeCardValue({ columnKey, scheme }: { columnKey: SchemeCardKey; scheme: CommissionScheme }) {
  switch (columnKey) {
    case 'model':
      return <>{scheme.model.name}</>;
    case 'personalRate':
      return <>{formatPercent(scheme.personalRatePercent)}</>;
    case 'regionalRate':
      return <>{formatPercent(scheme.regionalRatePercent)}</>;
    case 'newBusinessRate':
      return <>{newBusinessRateText(scheme) ?? '—'}</>;
    case 'newBusinessGate': {
      const overrides = newBusinessGateOverridesText(scheme);
      return (
        <>
          {newBusinessGateText(scheme) ?? '—'}
          {overrides && ` · ${overrides}`}
        </>
      );
    }
    case 'gate':
      return (
        <>
          {scheme.gateShape.name} · {formatPercent(scheme.personalGatePercent)} /{' '}
          {formatPercent(scheme.regionalGatePercent)}
        </>
      );
    case 'currency':
      return <>{scheme.currency}</>;
    case 'assignments':
      return <>{scheme.assignmentCount}</>;
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- *
 * Assignment row cells
 * -------------------------------------------------------------------------- */

/**
 * The dates that pro-rate an assignment.
 *
 * ⛔ **`null` MEANS THE WHOLE PERIOD AND MUST NOT RENDER AS A DASH.** Migration
 * 203: *"Both nullable. NULL means the whole period, which is the ordinary
 * case."* A dash reads as *no dates recorded*, which on a row that pro-rates a
 * target and a payout is a claim about missing data rather than about a full
 * year.
 *
 * ⚠️ **EITHER HALF ALONE IS A LEGAL HALF-OPEN RANGE** — joined in October and
 * still here, or here from the start and left in December — so all four
 * combinations are rendered rather than only the pair.
 *
 * ⚠️ **No `Date` is constructed from either.** `new Date('2026-05-31')` is
 * midnight UTC and renders as 30 May west of Greenwich; these are plain
 * `YYYY-MM-DD` and go through `formatDateOnly`.
 */
function DatesCell({ assignment }: { assignment: CommissionAssignment }) {
  const { startsOn, endsOn } = assignment;

  if (startsOn === null && endsOn === null) {
    return (
      <span
        className="text-sm text-fg-secondary"
        title="The whole period. Nothing is pro-rated on this assignment."
      >
        Whole period
      </span>
    );
  }

  return (
    <span className="whitespace-nowrap tabular text-sm">
      {startsOn === null ? (
        <span className="text-fg-muted">From the start</span>
      ) : (
        formatDateOnly(startsOn)
      )}
      <span className="mx-1.5 text-fg-subtle">→</span>
      {endsOn === null ? (
        <span className="text-fg-muted">To the end</span>
      ) : (
        formatDateOnly(endsOn)
      )}
    </span>
  );
}

/**
 * The regional commitment, or the fact that there is not one.
 *
 * 🔴 **`null` IS NOT ZERO, AND THE DIFFERENCE IS A YEAR'S PAY.** Migration 203:
 * *"A zero would make regional attainment a division by zero — NULL in MySQL,
 * falsy in a gate test, and therefore a silent permanent gate failure that looks
 * like underperformance. NULL says the question is not asked, the pool is not
 * computed, and the statement says so."*
 *
 * ⛔ So **never `?? 0`, never `?? '0.00'`, and never a dash** — a dash beside two
 * money columns reads as a figure nobody entered, where this is a decision
 * somebody took. A personal-only assignment is a real configuration.
 */
function RegionalTargetCell({ assignment }: { assignment: CommissionAssignment }) {
  if (assignment.regionalTarget === null) {
    return (
      <Badge
        tone="neutral"
        title="No regional commitment on this assignment, so no regional pool is computed for it."
      >
        No regional commitment
      </Badge>
    );
  }
  return <Money value={assignment.regionalTarget} currency={assignment.scheme.currency} />;
}

/** The assignment columns a card shows as a labelled `dl` row. */
type AssignmentCardKey = 'period' | 'scheme' | 'dates' | 'personalTarget' | 'regionalTarget';

const ASSIGNMENT_CARD_KEYS: ReadonlySet<string> = new Set<AssignmentCardKey>([
  'period',
  'scheme',
  'dates',
  'personalTarget',
  'regionalTarget',
]);

function isAssignmentCardKey(key: AssignmentColumnKey): key is AssignmentCardKey {
  return ASSIGNMENT_CARD_KEYS.has(key);
}

function AssignmentCardValue({
  columnKey,
  assignment,
}: {
  columnKey: AssignmentCardKey;
  assignment: CommissionAssignment;
}) {
  switch (columnKey) {
    case 'period':
      return <>{assignment.period.name}</>;
    case 'scheme':
      return <>{assignment.scheme.name}</>;
    case 'dates':
      return <DatesCell assignment={assignment} />;
    case 'personalTarget':
      return <Money value={assignment.personalTarget} currency={assignment.scheme.currency} />;
    case 'regionalTarget':
      return <RegionalTargetCell assignment={assignment} />;
    default:
      return null;
  }
}

function AssignmentCell({
  columnKey,
  assignment,
  onEdit,
  onDelete,
  editDenial,
  deleteDenial,
}: {
  columnKey: AssignmentColumnKey;
  assignment: CommissionAssignment;
  onEdit: () => void;
  onDelete: () => void;
  editDenial: string | null;
  deleteDenial: string | null;
}) {
  switch (columnKey) {
    case 'lead':
      /*
       * ⚠️ **Rendered defensively.** A staff record can carry an empty `name`,
       * and an empty string in a value slot reads as a MISSING record rather
       * than as a badly-filled one.
       */
      return (
        <div style={{ minWidth: NAME_MIN_CONTENT }}>
          <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
            {leadLabel(assignment.user)}
          </p>
          {assignment.notes && (
            <p className="mt-0.5 m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              {assignment.notes}
            </p>
          )}
        </div>
      );
    case 'period':
      return <span className="text-sm text-fg-secondary">{assignment.period.name}</span>;
    case 'scheme':
      return <span className="text-sm text-fg-secondary">{assignment.scheme.name}</span>;
    case 'dates':
      return <DatesCell assignment={assignment} />;
    case 'personalTarget':
      /*
       * ⚠️ **The currency is the SCHEME's, and there is no second conversion.**
       * Two rows on this page can legitimately be in two currencies, which is
       * why the code rides on every figure and why nothing here totals a column.
       *
       * ⚠️ **This is the FULL-YEAR figure.** Pro-rating happens at run time from
       * the dates two columns to the left and is frozen onto the statement; a
       * pro-rated number here would be a third copy of a fact two columns hold.
       */
      return <Money value={assignment.personalTarget} currency={assignment.scheme.currency} />;
    case 'regionalTarget':
      return <RegionalTargetCell assignment={assignment} />;
    case 'actions':
      return (
        <RowActions
          onEdit={onEdit}
          onDelete={onDelete}
          editDenial={editDenial}
          deleteDenial={deleteDenial}
          label={`the assignment for ${leadLabel(assignment.user)}`}
        />
      );
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- *
 * Column filters (owner 2026-10-25)
 * -------------------------------------------------------------------------- */

/*
 * One funnel per data column, through the house `useTableFilters`: a value
 * list over the column's DISPLAYED text, with Is empty / Is not empty. Every
 * row is loaded (see `fetchAllPages`), so the lists and the matching cover the
 * whole table. Money and percentages are matched as the formatted string —
 * never parsed. The keys are the column ids above; never rename one.
 */

function datesLabel(assignment: CommissionAssignment): string {
  const { startsOn, endsOn } = assignment;
  if (startsOn === null && endsOn === null) return 'Whole period';
  const from = startsOn === null ? 'From the start' : formatDateOnly(startsOn);
  const to = endsOn === null ? 'To the end' : formatDateOnly(endsOn);
  return `${from} → ${to}`;
}

function schemeColumnSpecs(
  rows: readonly CommissionScheme[],
  regionLabel: BandLabeller,
): ColumnSpec<CommissionScheme>[] {
  // Bands is a SET per row, so its options are the distinct bands and a row
  // matches when it measures any ticked one. A personal-only scheme has no
  // bands, so it is what Is empty finds.
  const bandCounts = new Map<string, number>();
  for (const row of rows) {
    for (const region of row.billingRegions) {
      bandCounts.set(region, (bandCounts.get(region) ?? 0) + 1);
    }
  }
  const bandOptions = Array.from(bandCounts, ([value, count]) => ({
    value,
    label: regionLabel(value),
    count,
  })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  return [
    { key: 'name', label: SCHEME_COLUMNS.name.label, get: (row) => row.name },
    { key: 'model', label: SCHEME_COLUMNS.model.label, get: (row) => row.model.name },
    {
      key: 'personalRate',
      label: SCHEME_COLUMNS.personalRate.label,
      numeric: true,
      get: (row) => formatPercent(row.personalRatePercent),
    },
    {
      key: 'regionalRate',
      label: SCHEME_COLUMNS.regionalRate.label,
      numeric: true,
      get: (row) => formatPercent(row.regionalRatePercent),
    },
    {
      key: 'newBusinessRate',
      label: SCHEME_COLUMNS.newBusinessRate.label,
      numeric: true,
      get: newBusinessRateText,
    },
    {
      key: 'newBusinessGate',
      label: SCHEME_COLUMNS.newBusinessGate.label,
      get: newBusinessGateText,
    },
    { key: 'gate', label: SCHEME_COLUMNS.gate.label, get: (row) => row.gateShape.name },
    { key: 'currency', label: SCHEME_COLUMNS.currency.label, get: (row) => row.currency },
    {
      key: 'bands',
      label: SCHEME_COLUMNS.bands.label,
      get: (row) => (schemeMeasuresRegions(row) ? row.billingRegions.join(',') : null),
      options: bandOptions,
      matches: (row, selected) => row.billingRegions.some((region) => selected.includes(region)),
    },
    {
      key: 'assignments',
      label: SCHEME_COLUMNS.assignments.label,
      numeric: true,
      get: (row) => String(row.assignmentCount),
    },
    {
      key: 'isActive',
      label: SCHEME_COLUMNS.isActive.label,
      get: (row) => (row.isActive ? 'Active' : 'Retired'),
    },
  ];
}

const ASSIGNMENT_COLUMN_SPECS: ColumnSpec<CommissionAssignment>[] = [
  { key: 'lead', label: ASSIGNMENT_COLUMNS.lead.label, get: (row) => leadLabel(row.user) },
  { key: 'period', label: ASSIGNMENT_COLUMNS.period.label, get: (row) => row.period.name },
  { key: 'scheme', label: ASSIGNMENT_COLUMNS.scheme.label, get: (row) => row.scheme.name },
  { key: 'dates', label: ASSIGNMENT_COLUMNS.dates.label, get: datesLabel },
  {
    key: 'personalTarget',
    label: ASSIGNMENT_COLUMNS.personalTarget.label,
    numeric: true,
    get: (row) => moneyText(row.personalTarget, row.scheme.currency),
  },
  {
    key: 'regionalTarget',
    label: ASSIGNMENT_COLUMNS.regionalTarget.label,
    numeric: true,
    // `null` is "no regional commitment", which is what Is empty finds.
    get: (row) => moneyText(row.regionalTarget, row.scheme.currency),
  },
];

/** The column filters hid every loaded row — distinct from an empty list. */
function ColumnFilterEmptyState({ noun, onClear }: { noun: string; onClear: () => void }) {
  return (
    <EmptyState
      icon={<CommissionIcon />}
      title={`No ${noun} match the column filters`}
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear column filters
        </Button>
      }
    />
  );
}

/* -------------------------------------------------------------------------- *
 * Shared chrome
 * -------------------------------------------------------------------------- */

/**
 * What went wrong with a list read, as a PANEL-LEVEL banner.
 *
 * ⛔ **A 403 CARRIES NO `details`, so it can never render per field** — there is
 * no field it belongs to. Keying it on `''` would make `fieldErrors` non-empty
 * and print *"one field needs attention, highlighted below"* with nothing
 * highlighted.
 *
 * ⚠️ **A 403 IS WORDED AS A REFUSAL, not as a failure.** *"Couldn't load"*
 * invites a retry that will be refused identically for as long as the grant is
 * absent. The server's own sentence is rendered beside ours rather than instead
 * of it: ours says which kind of answer this is, theirs says why.
 */
function LoadError({ failure, what }: { failure: LoadFailure; what: string }) {
  return (
    <Notice
      tone="danger"
      role="alert"
      className="m-3"
      title={
        failure.forbidden
          ? `Your account is not allowed to read ${what}.`
          : `Couldn’t load ${what}.`
      }
    >
      <span className="[overflow-wrap:anywhere]">{failure.message}</span>
    </Notice>
  );
}

interface LoadFailure {
  message: string;
  /** A refusal rather than a failure — see {@link LoadError}. */
  forbidden: boolean;
}

function toFailure(caught: unknown, fallback: string): LoadFailure {
  if (caught instanceof ApiError) {
    return { message: caught.message, forbidden: caught.status === 403 };
  }
  return {
    message: caught instanceof Error ? caught.message : fallback,
    forbidden: false,
  };
}

/**
 * The refusal a reader without the grant meets.
 *
 * ⛔ **THIS IS NOT "an administrator has hidden this screen".** That sentence
 * belongs to the nav surfaces, where the API behind the page answers everybody
 * the same and hiding it protects nothing. Here the grant IS the refusal — the
 * server answers a 403 — so the panel says what the reader may do next rather
 * than implying a screen was merely tidied away.
 *
 * ⚠️ **`denialReason` supplies the sentence**, so this page does not invent a
 * second wording for a refusal the ability model already words. It can return
 * `null` for a reader who IS allowed, which is why the caller only mounts this
 * when `canRead` is false and a fallback sentence stands behind it.
 */
function RefusalPanel({ reason }: { reason: string | null }) {
  return (
    <Panel>
      <EmptyState
        icon={<CommissionIcon />}
        title="Commissions are restricted"
        description={
          reason ??
          'Your account does not have permission to read commission schemes or assignments.'
        }
      />
    </Panel>
  );
}

/** The pager, identical on both sections so neither can drift from the other. */
function Pager({
  page,
  total,
  totalPages,
  onChange,
  noun,
  hiddenByFilters,
}: {
  page: number;
  total: number;
  totalPages: number;
  onChange: (next: number) => void;
  noun: string;
  /** Loaded rows the column filters are hiding. */
  hiddenByFilters: number;
}) {
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = Math.min(page * PER_PAGE, total);

  return (
    <PanelFooter>
      <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
        {total === 0 ? `No ${noun} to show` : `Showing ${firstRow}–${lastRow} of ${total}`}
        {hiddenByFilters > 0 && (
          <span className="text-fg-subtle"> · {hiddenByFilters} hidden by column filters</span>
        )}
      </p>
      <div className="flex items-center gap-2">
        <span className="hidden text-xs text-fg-subtle tabular sm:inline">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="secondary"
          size="sm"
          className="flex-1 sm:flex-none"
          leadingIcon={<ChevronIcon direction="left" />}
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="flex-1 sm:flex-none"
          trailingIcon={<ChevronIcon direction="right" />}
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </PanelFooter>
  );
}

/**
 * ⚠️ **`role="group"` with `aria-pressed`, NOT `role="tab"`.** These switch what
 * the section below IS rather than labelling panels that share one tabstop —
 * matching every other page-level view switch in this app. A real tablist would
 * promise arrow-key navigation between panels that do not share a tabstop.
 *
 * ⚠️ **Both pills render at every width, always.** This codebase has had a
 * working feature reported as *deleted* when it had merely become unreachable
 * behind a toggle. The selected pill is not distinguished by colour alone:
 * `secondary` gives it a raised surface against `ghost`'s transparent one, and
 * `aria-pressed` states it outright.
 */
function TabToggle({ value, onChange }: { value: Tab; onChange: (next: Tab) => void }) {
  return (
    <div
      role="group"
      aria-label="Commissions view"
      className="inline-flex shrink-0 flex-wrap items-center gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5"
    >
      {TABS.map((tab) => (
        <Button
          key={tab.value}
          size="sm"
          variant={value === tab.value ? 'secondary' : 'ghost'}
          aria-pressed={value === tab.value}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- *
 * The page
 * -------------------------------------------------------------------------- */

export function CommissionsPage() {
  const { ability } = useAuth();

  /*
   * ⚠️ `useSearchParams()` with NO argument. Its first parameter is a DEFAULT
   * INIT, not options — `useSearchParams({ replace: true })` injects a literal
   * `replace=true` into the query string and then into everything parsed out of
   * it. The `{ replace: true }` belongs on the setter.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();

  /*
   * The default is spelled by the key's ABSENCE, and NOTHING is written on
   * arrival — see the file header. Anything unrecognised reads as the default
   * too: an unknown pill is not a reason to show nothing.
   */
  const tab: Tab = searchParams.get(TAB_PARAM) === 'assignments' ? 'assignments' : 'schemes';

  const canRead = ability.canRead(COMMISSIONS);

  const changeTab = useCallback(
    (next: Tab, extra?: Readonly<Record<string, string>>) => {
      /*
       * 🔴 **ONE `URLSearchParams`, EVERY WRITE, ONE `setSearchParams`.** A
       * second call would start from this render's snapshot, which the first has
       * not updated yet, so whichever lands last is the only one that survives.
       * Nothing errors. This is why the Assignments cell hands its `schemeId`
       * in here rather than writing it itself.
       */
      const params = new URLSearchParams(searchParams);
      for (const key of CLEARED_ON_SWITCH) params.delete(key);
      if (next === 'schemes') params.delete(TAB_PARAM);
      else params.set(TAB_PARAM, next);
      if (extra) for (const [key, value] of Object.entries(extra)) params.set(key, value);
      /*
       * `{ replace: true }`: switching a pill is adjusting what is on screen,
       * not navigating, so it must not put a Back-button step between the reader
       * and the page they arrived from.
       */
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Commissions"
        /*
         * ⛔ **{@link COMMISSION_VISIBILITY_NOTE} IS NOT HELPER TEXT AND A SWEEP
         * MUST NOT TAKE IT.** It states a fact about the DATA that the reader
         * cannot otherwise see — that this screen discloses colleagues' pay and
         * that no lead can see any of it — and the wrong conclusion it prevents
         * ("this is an ordinary admin list") is one somebody acts on by leaving
         * it open on a shared screen. It is attached to a PAGE, not to a control.
         *
         * ⚠️ **`short` ON SCREEN, `detail` ON THE `title`, which is the shape
         * that module ships and not a truncation of it.** A heading carries one
         * line; the whole fact — *no sales lead sees a commission figure, not
         * their own* — is the half a reader needs when they stop to ask, and it
         * survives here rather than being dropped for width.
         */
        description={
          <span title={COMMISSION_VISIBILITY_NOTE.detail}>{COMMISSION_VISIBILITY_NOTE.short}</span>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <TabToggle value={tab} onChange={(next) => changeTab(next)} />
      </div>

      {/*
        ⛔ **THE REFUSAL REPLACES THE SECTION RATHER THAN SITTING ABOVE AN EMPTY
        TABLE.** An empty table under a heading reads as "there are no schemes",
        which is a statement about the business rather than about this reader's
        access — and on a module that discloses pay, the two must never be
        confused. Nothing is fetched on this branch either: a request certain to
        be refused spends from a budget shared by the whole office.

        ⚠️ **It is NOT the guarantee.** An absent scope resolves READ as allowed,
        so an unseeded catalogue or a failed `/permissions/me` lands on the
        sections below and the SERVER refuses — which `LoadError` words as a
        refusal rather than as a failure.
      */}
      {!canRead ? (
        <RefusalPanel reason={denialReason(ability, 'read', COMMISSIONS)} />
      ) : tab === 'schemes' ? (
        <SchemesSection queryString={queryString} onOpenAssignments={changeTab} />
      ) : (
        <AssignmentsSection queryString={queryString} />
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * Schemes
 * -------------------------------------------------------------------------- */

function SchemesSection({
  queryString,
  onOpenAssignments,
}: {
  queryString: string;
  onOpenAssignments: (next: Tab, extra?: Readonly<Record<string, string>>) => void;
}) {
  const { ability } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const regionLabel = useBandLabels();

  /** Every scheme the server-side narrowing returns — see `fetchAllPages`. */
  const [result, setResult] = useState<CommissionScheme[] | null>(null);
  const [search, setSearch] = useRememberedState('schemes-search', '');
  const [page, setPage] = useState(1);
  const [failure, setFailure] = useState<LoadFailure | null>(null);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionScheme | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CommissionScheme | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ReactNode>(null);
  /*
   * A refused delete cannot succeed on a second press — a scheme an assignment
   * names is refused by `ON DELETE RESTRICT` until the assignment moves — so the
   * confirm button is held down BESIDE the sentence that says why. `ConfirmDialog`
   * documents that pairing: a bare disabled button makes a refusal, a permission
   * denial and an ordinary state look the same.
   */
  const [deleteRefused, setDeleteRefused] = useState(false);

  const isActive = parseTriFlag(new URLSearchParams(queryString).get('isActive'));

  const setFilter = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(queryString);
      // ⚠️ An empty value DELETES the key. `?isActive=` is a 422 on a `.strict()`
      // schema, not "no filter".
      if (value === '') params.delete(key);
      else params.set(key, value);
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  /**
   * 🔴 **RESET TO PAGE 1 ON EVERY NARROWING.** Filtering from page 3 asks for
   * page 3 of a much shorter result — usually empty, which reads as *"no schemes
   * match"* when the matches are all on page 1.
   */
  const rows = useMemo(() => result ?? [], [result]);
  const columnSpecs = useMemo(() => schemeColumnSpecs(rows, regionLabel), [rows, regionLabel]);
  const columnFilters = useTableFilters(rows, columnSpecs, { memoryId: 'commission-schemes' });
  const columnFilterKey = filterSignature(SCHEME_COLUMN_KEYS, columnFilters.headerFor);

  const columnLayout = useColumnLayout(
    SCHEME_LAYOUT_SCOPE,
    SCHEME_COLUMN_KEYS,
    LOCKED_KEYS,
    PINNED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const columnOrder = useMemo(
    () => renderOrder(columnLayout.visibleKeys),
    [columnLayout.visibleKeys],
  ) as SchemeColumnKey[];
  const schemeCardKeys = columnOrder.filter(isSchemeCardKey);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    columnOrder,
    columnLayout.frozenSideOf,
    PINNED_KEYS,
  );
  const frozen = useMemo<FrozenModel>(
    () => ({ frozenSideOf: columnLayout.frozenSideOf, frozenOffsets }),
    [columnLayout.frozenSideOf, frozenOffsets],
  );
  const minWidth = tableFloor(columnOrder, SCHEME_COLUMNS);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setPage(1);
  }, [search, isActive, columnFilterKey]);

  /*
   * No `page` here: every page is read (see `fetchAllPages`) and the pager
   * slices locally, so paging never refetches.
   */
  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: CommissionSchemeListParams = { perPage: FETCH_PER_PAGE };
      if (search) params.search = search.slice(0, MAX_SEARCH);
      if (isActive) params.isActive = isActive === 'true';

      return fetchAllPages((page) => api.listCommissionSchemes({ ...params, page }, signal))
        .then((data) => {
          setResult(data);
          setFailure(null);
        })
        .catch((caught: unknown) => {
          // ⚠️ Guarded in BOTH arms: an aborted request is not a failure, and
          // reporting one flashes an error banner on every keystroke.
          if (signal?.aborted) return;
          setFailure(toFailure(caught, 'Failed to load commission schemes'));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [search, isActive],
  );

  /*
   * Debounced at 250ms so typing does not fire a request per keystroke, and the
   * controller aborts the in-flight one when the term moves on. The limiter is
   * PER IP, shared by everyone behind the office NAT.
   * ⚠️ *SUPERSEDED: "120 requests per 60 seconds."* It is **12,000/60s**
   * (corrected 2026-09-17).
   */
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  const visibleRows = columnFilters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;
  const total = visibleRows.length;
  const totalPages = Math.max(Math.ceil(total / PER_PAGE), 1);
  // Clamped rather than reset, so a delete that empties the last page lands on
  // the new last page instead of an empty one.
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleRows.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  /* The first paint only — a refetch under an existing result dims the rows
     rather than replacing them, so the page does not flash. */
  const initialLoad = loading && result === null;
  const filtered = search !== '' || isActive !== '';

  const canCreate = ability.canCreate(COMMISSIONS);
  const createDenial = denialReason(ability, 'create', COMMISSIONS);
  const editDenial = denialReason(ability, 'update', COMMISSIONS);
  const deleteDenial = denialReason(ability, 'delete', COMMISSIONS);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    setDeleteRefused(false);
    void api
      .deleteCommissionScheme(pendingDelete.id)
      .then(() => {
        setPendingDelete(null);
        void load();
      })
      .catch((caught: unknown) => {
        /*
         * ⛔ **THE SERVER'S SENTENCE, VERBATIM, PLUS THE WAY OUT.** A scheme an
         * assignment names is a 409 and the message names the assignments; that
         * is the only actionable half and nothing of ours may stand in front of
         * it. What we add is the supported removal — retiring with `isActive:
         * false`, which keeps the scheme resolving on every assignment and
         * statement that already names it.
         */
        const refused = caught instanceof ApiError && caught.status === 409;
        setDeleteRefused(refused);
        setDeleteError(
          <>
            <p className="m-0">
              {caught instanceof Error ? caught.message : 'The scheme could not be deleted.'}
            </p>
            {refused && (
              <p className="mt-1.5 m-0">
                Retire it instead — an inactive scheme stays readable on every assignment and
                statement that already names it.
              </p>
            )}
          </>,
        );
      })
      .finally(() => setDeleting(false));
  };

  return (
    <>
      <Panel>
        {failure && <LoadError failure={failure} what="commission schemes" />}

        {/*
          ⚠️ **{@link TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE} IS A FACT, NOT
          HELPER TEXT.** Editing a scheme reaches every assignment on it that has
          not been run — approval freezes a RUN and protects a year already paid,
          and protects nothing about a year configured and not yet run. Nothing
          else on this screen says so, and the wrong conclusion it prevents ("a
          template is a one-time seed") is one somebody acts on by correcting a
          rate and believing it applies to next year alone.
        */}
        <p
          className="m-0 border-b border-border bg-surface-sunken px-4 py-2 text-xs text-fg-muted"
          title={TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE.detail}
        >
          {TEMPLATE_EDIT_REACHES_ASSIGNMENTS_NOTE.short}
        </p>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-56">
            <Field label="Search schemes" hideLabel>
              {({ id }) => (
                /*
                 * ⚠️ A LITERAL ellipsis character in the placeholder, never a
                 * backslash-u escape of it: a double-quoted JSX attribute is a
                 * JSX string and NOT a JavaScript one, so an escape sequence
                 * there reaches the DOM as the characters somebody typed rather
                 * than as the glyph. (Described in words rather than shown — a
                 * rule about a token cannot be illustrated with the token.)
                 */
                <Input
                  id={id}
                  type="search"
                  placeholder="Search scheme names…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="w-full min-w-0 sm:w-44">
            <Field label="Filter by status" hideLabel>
              {({ id }) => (
                <Combobox
                  id={id}
                  value={isActive}
                  onChange={(value) => setFilter('isActive', value)}
                  options={[
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Retired' },
                  ]}
                  /* `''` is a THIRD request — it omits the key rather than
                     asking either question. */
                  emptyOptionLabel="Active and retired"
                  placeholder="Active and retired"
                />
              )}
            </Field>
          </div>

          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setFilter('isActive', '');
              }}
            >
              Clear
            </Button>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* The card list has no headers, so its funnels live here. */}
            <FilterSheet
              className="md:hidden"
              keys={SCHEME_COLUMN_KEYS}
              headerFor={columnFilters.headerFor}
              activeCount={columnFilters.activeCount}
              clearAll={columnFilters.clearAll}
            />
            <ColumnManager
              layout={columnLayout}
              labelOf={schemeLabelOf}
              lockedKeys={LOCKED_KEYS}
              lockReasonOf={actionsLockReason}
              pinnedKeys={PINNED_KEYS}
            />
            {/*
              ⚠️ **DISABLED WITH THE REASON ON A WRAPPER `span`, not hidden.** A
              reader who may read this page and may not write to it should be
              told which, rather than meeting a screen that looks read-only for
              no stated reason — and a `title` on a disabled `Button` never
              renders, because `Button` carries `disabled:pointer-events-none`.
            */}
            <span {...(createDenial ? { title: createDenial } : {})}>
              <Button
                size="sm"
                leadingIcon={<PlusIcon />}
                disabled={!canCreate}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                New scheme
              </Button>
            </span>
          </div>
        </div>

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* ── Below `md`: a stacked card per row ─────────────────────────────
              Ten columns will not fit 320px, and every one of them rides on the
              card: a column that exists only above a breakpoint is
              indistinguishable from one that was removed. */}
          <div className="md:hidden">
            {initialLoad ? (
              <SkeletonCards />
            ) : rows.length === 0 ? (
              <SchemesEmptyState filtered={filtered} failure={failure} />
            ) : visibleRows.length === 0 ? (
              <ColumnFilterEmptyState noun="schemes" onClear={columnFilters.clearAll} />
            ) : (
              <ul className="m-0 list-none p-0">
                {pageRows.map((scheme) => (
                  <li key={scheme.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    {/* The card honours the column manager: a hidden column is
                        absent here too, and the fields follow its order. */}
                    {(columnLayout.isVisible('name') || columnLayout.isVisible('isActive')) && (
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          {columnLayout.isVisible('name') && (
                            <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                              {scheme.name}
                            </p>
                          )}
                        </div>
                        {columnLayout.isVisible('isActive') &&
                          (scheme.isActive ? (
                            <Badge tone="success">Active</Badge>
                          ) : (
                            <Badge tone="neutral">Retired</Badge>
                          ))}
                      </div>
                    )}

                    {/*
                      ⚠️ **LABELLED, unlike the desktop cells, and that is the
                      difference between a table and a card rather than an
                      inconsistency.** Four percentages under a heading row mean
                      something; four percentages floating on a card mean
                      nothing. A label NAMES a value rather than explaining it,
                      so it is not helper text.
                    */}
                    {schemeCardKeys.length > 0 && (
                      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        {schemeCardKeys.map((key) => (
                          <div key={key} className="contents">
                            <dt className="m-0 text-fg-muted">{SCHEME_COLUMNS[key].label}</dt>
                            <dd className="m-0 text-right text-fg-secondary tabular">
                              <SchemeCardValue columnKey={key} scheme={scheme} />
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}

                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      {columnLayout.isVisible('bands') ? (
                        <BandsCell scheme={scheme} regionLabel={regionLabel} />
                      ) : (
                        <span />
                      )}
                      <RowActions
                        onEdit={() => {
                          setEditing(scheme);
                          setFormOpen(true);
                        }}
                        onDelete={() => {
                          setDeleteError(null);
                          setDeleteRefused(false);
                          setPendingDelete(scheme);
                        }}
                        editDenial={editDenial}
                        deleteDenial={deleteDenial}
                        label={scheme.name}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── From `md`: the table ───────────────────────────────────────────
              ONE mount, with only the `<TBody>` swapping. Two branches would
              mean two header rows and, with them, two measured layouts. */}
          <div className="hidden md:block">
            {!initialLoad && rows.length === 0 ? (
              <SchemesEmptyState filtered={filtered} failure={failure} />
            ) : (
              <>
              {/* The header stays mounted when the column filters empty the
                  table, so the funnel that did it is still there to undo. */}
              <TableWrap frozenHeader>
                <Table style={{ minWidth }}>
                  <THead>
                    <TR ref={headRowRef}>
                      {columnOrder.map((key) =>
                        key === ACTIONS_KEY ? (
                          <TH key={key} numeric pinned>
                            <span className="sr-only">Row actions</span>
                          </TH>
                        ) : (
                          <FilterableTH
                            key={key}
                            {...columnFilters.headerFor(key)}
                            {...frozenPropsFor(key, frozen)}
                          />
                        ),
                      )}
                    </TR>
                  </THead>

                  {initialLoad ? (
                    <TBody>
                      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                        <TR key={index}>
                          {columnOrder.map((key) => (
                            <TD
                              key={key}
                              numeric={SCHEME_COLUMNS[key].numeric}
                              pinned={key === ACTIONS_KEY}
                              {...frozenPropsFor(key, frozen)}
                            >
                              <Skeleton className="h-3 w-24" />
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  ) : (
                    <TBody>
                      {pageRows.map((scheme) => (
                        <TR key={scheme.id} interactive>
                          {columnOrder.map((key) => (
                            <TD
                              key={key}
                              numeric={SCHEME_COLUMNS[key].numeric}
                              pinned={key === ACTIONS_KEY}
                              {...frozenPropsFor(key, frozen)}
                            >
                              <SchemeCell
                                columnKey={key}
                                scheme={scheme}
                                regionLabel={regionLabel}
                                editDenial={editDenial}
                                deleteDenial={deleteDenial}
                                onEdit={() => {
                                  setEditing(scheme);
                                  setFormOpen(true);
                                }}
                                onDelete={() => {
                                  setDeleteError(null);
                                  setDeleteRefused(false);
                                  setPendingDelete(scheme);
                                }}
                                onOpenAssignments={(schemeId) =>
                                  onOpenAssignments('assignments', {
                                    schemeId: String(schemeId),
                                  })
                                }
                              />
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  )}
                </Table>
              </TableWrap>
              {!initialLoad && visibleRows.length === 0 && (
                <ColumnFilterEmptyState noun="schemes" onClear={columnFilters.clearAll} />
              )}
              </>
            )}
          </div>
        </div>

        {result && (
          <Pager
            page={currentPage}
            total={total}
            totalPages={totalPages}
            onChange={setPage}
            hiddenByFilters={hiddenByFilters}
            noun="schemes"
          />
        )}
      </Panel>

      <SchemeFormModal
        open={formOpen}
        scheme={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          void load();
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete this commission scheme?"
        /*
         * ⛔ **NAME THE RECORD AND THE REAL CONSEQUENCE.** The band rows cascade
         * with the scheme (migration 202) — they are part of its definition and
         * mean nothing without it — and the assignments do NOT: they hold the
         * scheme down with `ON DELETE RESTRICT` and the delete is refused.
         */
        description={
          pendingDelete && (
            <>
              <p className="m-0">
                <strong className="font-medium text-fg">{pendingDelete.name}</strong> and the billing
                bands it measures will be deleted. This cannot be undone.
              </p>
              {pendingDelete.assignmentCount > 0 && (
                <p className="mt-1.5 m-0">
                  {pluralise(pendingDelete.assignmentCount, 'lead is', 'leads are')} assigned to it,
                  so the server will refuse this.
                </p>
              )}
            </>
          )
        }
        loading={deleting}
        error={deleteError}
        confirmDisabled={deleteRefused}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * Assignments
 * -------------------------------------------------------------------------- */

function AssignmentsSection({ queryString }: { queryString: string }) {
  const { ability } = useAuth();
  const [, setSearchParams] = useSearchParams();

  /** Every assignment the server-side narrowing returns — see `fetchAllPages`. */
  const [result, setResult] = useState<CommissionAssignment[] | null>(null);
  const [page, setPage] = useState(1);
  const [failure, setFailure] = useState<LoadFailure | null>(null);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CommissionAssignment | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CommissionAssignment | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ReactNode>(null);

  const filters = useMemo(() => {
    const current = new URLSearchParams(queryString);
    const schemeIds = parseIdList(current.get('schemeId'));
    return {
      schemeIds,
      /* A string, so effects and memos key on the VALUE of the list. */
      schemeKey: schemeIds.join(','),
      userId: parseId(current.get('userId')),
      periodId: parseId(current.get('periodId')),
    };
  }, [queryString]);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(queryString);
      if (value === '') params.delete(key);
      else params.set(key, value);
      setSearchParams(params, { replace: true });
    },
    [queryString, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const params = new URLSearchParams(queryString);
    for (const key of ['schemeId', 'userId', 'periodId']) params.delete(key);
    setSearchParams(params, { replace: true });
  }, [queryString, setSearchParams]);

  /*
   * ⚠️ **MEMOISED ON `result`, because `?? []` ALONE IS A FRESH ARRAY EVERY
   * RENDER.** `pickerOptions` and the column filters take it as a dependency,
   * so an unmemoised `rows` keys those memos on identity and rebuilds them on
   * every render for ever.
   */
  const rows = useMemo(() => result ?? [], [result]);
  const columnFilters = useTableFilters(rows, ASSIGNMENT_COLUMN_SPECS, {
    memoryId: 'commission-assignments',
  });
  const columnFilterKey = filterSignature(ASSIGNMENT_COLUMN_KEYS, columnFilters.headerFor);

  const columnLayout = useColumnLayout(
    ASSIGNMENT_LAYOUT_SCOPE,
    ASSIGNMENT_COLUMN_KEYS,
    LOCKED_KEYS,
    PINNED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const columnOrder = useMemo(
    () => renderOrder(columnLayout.visibleKeys),
    [columnLayout.visibleKeys],
  ) as AssignmentColumnKey[];
  const assignmentCardKeys = columnOrder.filter(isAssignmentCardKey);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    columnOrder,
    columnLayout.frozenSideOf,
    PINNED_KEYS,
  );
  const frozen = useMemo<FrozenModel>(
    () => ({ frozenSideOf: columnLayout.frozenSideOf, frozenOffsets }),
    [columnLayout.frozenSideOf, frozenOffsets],
  );
  const minWidth = tableFloor(columnOrder, ASSIGNMENT_COLUMNS);

  /** 🔴 Reset to page 1 on every narrowing — see the schemes section. */
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setPage(1);
  }, [filters.schemeKey, filters.userId, filters.periodId, columnFilterKey]);

  /* No `page` here — every page is read and the pager slices locally. */
  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      const params: CommissionAssignmentListParams = { perPage: FETCH_PER_PAGE };
      if (filters.schemeKey !== '') params.schemeId = filters.schemeKey;
      if (filters.userId !== null) params.userId = filters.userId;
      if (filters.periodId !== null) params.periodId = filters.periodId;

      return fetchAllPages((page) => api.listCommissionAssignments({ ...params, page }, signal))
        .then((data) => {
          setResult(data);
          setFailure(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setFailure(toFailure(caught, 'Failed to load commission assignments'));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [filters.schemeKey, filters.userId, filters.periodId],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  const schemeOptions = useSchemeFilterOptions();

  /**
   * Keeps the chosen scheme renderable when its id is not in the loaded options.
   *
   * 🔴 **A `Combobox` RENDERS ITS PLACEHOLDER FOR A VALUE ABSENT FROM ITS
   * OPTIONS**, where a native `<select>` shows the value as-is. So an id that
   * arrived on the URL before the options did — or names a scheme past the first
   * page — makes the control **silently misreport its own state**: it reads
   * "All schemes" over a filtered table. The loaded rows are the best label
   * source available; failing that the bare id is shown, because an empty
   * trigger over a narrowed list is the wrong answer in the reassuring direction.
   */
  const pickerOptions = useMemo(() => {
    const options = [...schemeOptions.options];
    for (const id of filters.schemeIds) {
      const chosen = String(id);
      if (options.some((option) => option.value === chosen)) continue;
      const named = rows.find((row) => String(row.scheme.id) === chosen);
      options.unshift({ value: chosen, label: named ? named.scheme.name : `Scheme #${chosen}` });
    }
    return options;
  }, [schemeOptions.options, filters.schemeIds, rows]);

  const visibleRows = columnFilters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;
  const total = visibleRows.length;
  const totalPages = Math.max(Math.ceil(total / PER_PAGE), 1);
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleRows.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const initialLoad = loading && result === null;
  const filtered =
    filters.schemeIds.length > 0 || filters.userId !== null || filters.periodId !== null;

  const canCreate = ability.canCreate(COMMISSIONS);
  const createDenial = denialReason(ability, 'create', COMMISSIONS);
  const editDenial = denialReason(ability, 'update', COMMISSIONS);
  const deleteDenial = denialReason(ability, 'delete', COMMISSIONS);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    void api
      .deleteCommissionAssignment(pendingDelete.id)
      .then(() => {
        setPendingDelete(null);
        void load();
      })
      .catch((caught: unknown) => {
        setDeleteError(
          caught instanceof Error ? caught.message : 'The assignment could not be deleted.',
        );
      })
      .finally(() => setDeleting(false));
  };

  return (
    <>
      <Panel>
        {failure && <LoadError failure={failure} what="commission assignments" />}

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field
              label="Filter by scheme"
              hideLabel
              /* The invalid border alone says nothing; a failed master needs
                 words beside it, or an empty list reads as "there are no
                 schemes" rather than as a read that did not come back. */
              {...(schemeOptions.failed ? { error: 'Couldn’t load schemes.' } : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  values={filters.schemeIds.map(String)}
                  onChange={(values) => setFilter('schemeId', values.join(','))}
                  options={pickerOptions}
                  placeholder="All schemes"
                  searchPlaceholder="Search schemes…"
                  summaryNoun={{ singular: 'scheme', plural: 'schemes' }}
                  loading={schemeOptions.loading}
                  aria-describedby={describedBy}
                  {...(schemeOptions.failed ? { invalid: true } : {})}
                  {...(schemeOptions.hasMore
                    ? { footnote: `Showing the first ${OPTION_PER_PAGE} schemes.` }
                    : {})}
                />
              )}
            </Field>
          </div>

          {filtered && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <FilterSheet
              className="md:hidden"
              keys={ASSIGNMENT_COLUMN_KEYS}
              headerFor={columnFilters.headerFor}
              activeCount={columnFilters.activeCount}
              clearAll={columnFilters.clearAll}
            />
            <ColumnManager
              layout={columnLayout}
              labelOf={assignmentLabelOf}
              lockedKeys={LOCKED_KEYS}
              lockReasonOf={actionsLockReason}
              pinnedKeys={PINNED_KEYS}
            />
            <span {...(createDenial ? { title: createDenial } : {})}>
              <Button
                size="sm"
                leadingIcon={<PlusIcon />}
                disabled={!canCreate}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                New assignment
              </Button>
            </span>
          </div>
        </div>

        {/*
          The two id filters that are arrived at BY LINK and have no control of
          their own. A badge each, so a filtered list never looks unfiltered.
        */}
        {(filters.userId !== null || filters.periodId !== null) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 text-xs text-fg-muted">
            <span>Also filtered by</span>
            {filters.userId !== null && <Badge tone="neutral" variant="outline">Lead #{filters.userId}</Badge>}
            {filters.periodId !== null && <Badge tone="neutral" variant="outline">Period #{filters.periodId}</Badge>}
          </div>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          <div className="md:hidden">
            {initialLoad ? (
              <SkeletonCards />
            ) : rows.length === 0 ? (
              <AssignmentsEmptyState filtered={filtered} failure={failure} />
            ) : visibleRows.length === 0 ? (
              <ColumnFilterEmptyState noun="assignments" onClear={columnFilters.clearAll} />
            ) : (
              <ul className="m-0 list-none p-0">
                {pageRows.map((assignment) => (
                  <li
                    key={assignment.id}
                    className="border-b border-border px-4 py-3 last:border-b-0"
                  >
                    {/* Honours the column manager, in its order, like the table. */}
                    {columnLayout.isVisible('lead') && (
                      <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                        {leadLabel(assignment.user)}
                      </p>
                    )}

                    {assignmentCardKeys.length > 0 && (
                      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        {assignmentCardKeys.map((key) => (
                          <div key={key} className="contents">
                            <dt className="m-0 text-fg-muted">{ASSIGNMENT_COLUMNS[key].label}</dt>
                            <dd className="m-0 text-right text-fg-secondary">
                              <AssignmentCardValue columnKey={key} assignment={assignment} />
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}

                    <div className="mt-2 flex justify-end">
                      <RowActions
                        onEdit={() => {
                          setEditing(assignment);
                          setFormOpen(true);
                        }}
                        onDelete={() => {
                          setDeleteError(null);
                          setPendingDelete(assignment);
                        }}
                        editDenial={editDenial}
                        deleteDenial={deleteDenial}
                        label={`the assignment for ${leadLabel(assignment.user)}`}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hidden md:block">
            {!initialLoad && rows.length === 0 ? (
              <AssignmentsEmptyState filtered={filtered} failure={failure} />
            ) : (
              <>
              <TableWrap frozenHeader>
                <Table style={{ minWidth }}>
                  <THead>
                    <TR ref={headRowRef}>
                      {columnOrder.map((key) =>
                        key === ACTIONS_KEY ? (
                          <TH key={key} numeric pinned>
                            <span className="sr-only">Row actions</span>
                          </TH>
                        ) : (
                          <FilterableTH
                            key={key}
                            {...columnFilters.headerFor(key)}
                            {...frozenPropsFor(key, frozen)}
                          />
                        ),
                      )}
                    </TR>
                  </THead>

                  {initialLoad ? (
                    <TBody>
                      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                        <TR key={index}>
                          {columnOrder.map((key) => (
                            <TD
                              key={key}
                              numeric={ASSIGNMENT_COLUMNS[key].numeric}
                              pinned={key === ACTIONS_KEY}
                              {...frozenPropsFor(key, frozen)}
                            >
                              <Skeleton className="h-3 w-24" />
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  ) : (
                    <TBody>
                      {pageRows.map((assignment) => (
                        <TR key={assignment.id} interactive>
                          {columnOrder.map((key) => (
                            <TD
                              key={key}
                              numeric={ASSIGNMENT_COLUMNS[key].numeric}
                              pinned={key === ACTIONS_KEY}
                              {...frozenPropsFor(key, frozen)}
                            >
                              <AssignmentCell
                                columnKey={key}
                                assignment={assignment}
                                editDenial={editDenial}
                                deleteDenial={deleteDenial}
                                onEdit={() => {
                                  setEditing(assignment);
                                  setFormOpen(true);
                                }}
                                onDelete={() => {
                                  setDeleteError(null);
                                  setPendingDelete(assignment);
                                }}
                              />
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  )}
                </Table>
              </TableWrap>
              {!initialLoad && visibleRows.length === 0 && (
                <ColumnFilterEmptyState noun="assignments" onClear={columnFilters.clearAll} />
              )}
              </>
            )}
          </div>
        </div>

        {result && (
          <Pager
            page={currentPage}
            total={total}
            totalPages={totalPages}
            onChange={setPage}
            hiddenByFilters={hiddenByFilters}
            noun="assignments"
          />
        )}
      </Panel>

      <AssignmentFormModal
        open={formOpen}
        assignment={editing}
        /*
         * ⚠️ **HANDED OVER RATHER THAN RE-READ.** The form decides whether a
         * regional target is REQUIRED from the chosen scheme's `billingRegions`,
         * and its own doc records that two independent reads of this list can
         * disagree about that — surfacing as a refusal on a field the form had
         * just called optional. This page already holds the list for its filter.
         *
         * ⚠️ **`schemesFailed` TRAVELS WITH IT, and dropping it would be the
         * whole of the damage.** An empty array alone reads as *there are no
         * schemes to assign anybody to*; with the flag it reads as *we could not
         * ask*, which is a different sentence and a different next step.
         */
        schemes={schemeOptions.schemes}
        schemesFailed={schemeOptions.failed}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          void load();
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete this assignment?"
        description={
          pendingDelete && (
            <p className="m-0">
              <strong className="font-medium text-fg">
                {leadLabel(pendingDelete.user)}
              </strong>{' '}
              will no longer be on{' '}
              <strong className="font-medium text-fg">{pendingDelete.scheme.name}</strong> for{' '}
              {pendingDelete.period.name}, and the targets recorded against that year will be
              deleted with it. This cannot be undone.
            </p>
          )
        }
        loading={deleting}
        error={deleteError}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * The scheme filter's options
 * -------------------------------------------------------------------------- */

interface SchemeOptionSource {
  /**
   * The records themselves, handed to `AssignmentFormModal`.
   *
   * ⚠️ **ONE READ, ONE ANSWER ABOUT `billingRegions`.** That form decides whether
   * a regional target is REQUIRED from the chosen scheme's bands, and its own
   * doc says two independent reads of this list can disagree about that — the
   * disagreement surfacing as a refusal on a field the form had just called
   * optional. The page already holds the list for its filter, so handing it over
   * costs nothing and removes the second read entirely.
   */
  schemes: readonly CommissionScheme[];
  options: ComboboxOption[];
  loading: boolean;
  failed: boolean;
  /** More schemes exist than the one page this picker holds. */
  hasMore: boolean;
}

/**
 * Schemes, for the assignments filter.
 *
 * ⛔ **`isActive` IS DELIBERATELY NOT SENT.** A FILTER offers what can MATCH; a
 * PICKER offers what can be CHOSEN. An assignment can legitimately name a
 * retired scheme — retiring is the supported removal here, precisely because a
 * scheme an assignment names cannot be deleted — so narrowing this list to
 * active schemes would make those assignments unreachable through the one
 * control that could find them. The scheme FORM's picker is where narrowing
 * belongs, and it is not this.
 *
 * ⚠️ **`hasMore` is reported rather than swallowed.** A picker missing its 101st
 * option is indistinguishable from that scheme not existing, and 100 is the
 * endpoint's own ceiling — so the fix is a footnote or paging, never a bigger
 * `perPage`.
 */
function useSchemeFilterOptions(): SchemeOptionSource {
  const [schemes, setSchemes] = useState<readonly CommissionScheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCommissionSchemes({ page: 1, perPage: OPTION_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setSchemes(result.data);
        setHasMore(result.meta.total > result.data.length);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        /*
         * ⛔ **EMPTIED AND FLAGGED, NEVER EMPTIED ALONE.** An empty list with no
         * `failed` beside it says *there are no schemes*, which is a claim about
         * the business made on the strength of a request that never came back.
         */
        setSchemes([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const options = useMemo(
    () =>
      schemes.map((scheme) => ({
        value: String(scheme.id),
        label: scheme.name,
        // Two schemes can differ only in their rates, so the currency is what
        // tells a reader which one they are picking.
        hint: scheme.currency,
      })),
    [schemes],
  );

  return { schemes, options, loading, failed, hasMore };
}

/* -------------------------------------------------------------------------- *
 * Empty states
 * -------------------------------------------------------------------------- */

/** Skeletons in the SHAPE of a card, never a spinner. */
function SkeletonCards() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <li key={index} className="border-b border-border px-4 py-3 last:border-b-0">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-44" />
            <Skeleton className="h-2.5 w-28" />
            <Skeleton className="h-2.5 w-36" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * ⛔ **NO COPY HERE IMPLIES DATA IS ON ITS WAY.** A blank list is a permanent,
 * ordinary state in this app — a commission scheme is a template somebody
 * creates, not a record that accumulates — and an empty state that hedges about
 * it is how a dead endpoint gets mistaken for a quiet week: `GET /deliverables`
 * returned a `500` for a day and rendered exactly the documented empty state.
 *
 * ⚠️ **A REFUSED READ GETS ITS OWN BRANCH**, and it does not offer to clear
 * filters: clearing a filter cannot make a 403 into a 200, and a button whose
 * only outcome is the same refusal is the affordance this repo refuses
 * everywhere.
 */
function SchemesEmptyState({
  filtered,
  failure,
}: {
  filtered: boolean;
  failure: LoadFailure | null;
}) {
  if (failure) {
    return (
      <EmptyState
        icon={<CommissionIcon />}
        title="Nothing to show"
        description={
          failure.forbidden
            ? 'Commission schemes were not read, because your account is not allowed to read them.'
            : 'The scheme list couldn’t be loaded. Check the message above and try again.'
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<CommissionIcon />}
      title={filtered ? 'No schemes match those filters' : 'No commission schemes yet'}
      description={
        filtered
          ? 'Search matches the scheme name.'
          : 'A scheme names the terms — the rates, the gate and the currency — and nobody in particular.'
      }
    />
  );
}

function AssignmentsEmptyState({
  filtered,
  failure,
}: {
  filtered: boolean;
  failure: LoadFailure | null;
}) {
  if (failure) {
    return (
      <EmptyState
        icon={<CommissionIcon />}
        title="Nothing to show"
        description={
          failure.forbidden
            ? 'Commission assignments were not read, because your account is not allowed to read them.'
            : 'The assignment list couldn’t be loaded. Check the message above and try again.'
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<CommissionIcon />}
      title={filtered ? 'No assignments match those filters' : 'Nobody is assigned yet'}
      description={
        filtered
          ? 'An assignment names one lead, one scheme and one financial year.'
          : 'An assignment puts a lead on a scheme for a financial year, with the two targets on their ladder.'
      }
    />
  );
}
